import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { HiChevronRight } from 'react-icons/hi';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { VALUES } from '../utils/constants';

const About = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: storyRef, isVisible: storyVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation({ threshold: 0.3 });

  return (
    <>
      <Helmet>
        <title>About Us | Our Story & Mission | Rishant Technologies</title>
        <meta name="description" content="Learn about Rishant Technologies - founded to make enterprise-level AI automation accessible to Indian small businesses." />
        <link rel="canonical" href="https://rishant.tech/about" />
      </Helmet>

      <main style={{ paddingTop: '80px' }}>
        {/* Header */}
        <section ref={headerRef} style={{ backgroundColor: '#F5F0E8', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={headerVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ maxWidth: '720px' }}
            >
              <h1 style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 700,
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
                color: '#1A1A1A',
                fontFamily: '"DM Sans", sans-serif',
                marginBottom: '1.5rem',
              }}>
                Built by{' '}
                <span style={{ textDecoration: 'underline', textDecorationThickness: '3px', textUnderlineOffset: '6px' }}>
                  technologists
                </span>
                . Run by people who get it.
              </h1>
              <p style={{
                fontSize: '1.15rem',
                lineHeight: 1.7,
                color: '#666666',
                fontFamily: '"DM Sans", sans-serif',
              }}>
                We're not another Silicon Valley startup selling generic tools. We're Indians who understand how Indian businesses actually work.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Founder Story */}
        <section ref={storyRef} style={{ backgroundColor: '#FAF7F2', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '3rem' }} className="about-story-grid">
              {/* Left: story */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={storyVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <h2 style={{
                  fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: '#1A1A1A',
                  marginBottom: '1.5rem',
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  The problem we saw
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <p style={{ fontSize: '1rem', lineHeight: 1.75, color: '#333333', fontFamily: '"DM Sans", sans-serif' }}>
                    After 15+ years in software engineering, our founder noticed something that bothered her: amazing automation technology was only available to large enterprises who could afford six-figure implementations.
                  </p>
                  <p style={{ fontSize: '1rem', lineHeight: 1.75, color: '#333333', fontFamily: '"DM Sans", sans-serif' }}>
                    Meanwhile, small and medium businesses in India — the backbone of our economy — were still drowning in manual processes, losing customers to slow responses, and burning out their best employees on repetitive work.
                  </p>
                  <p style={{ fontSize: '1rem', lineHeight: 1.75, color: '#333333', fontFamily: '"DM Sans", sans-serif' }}>
                    <strong style={{ color: '#1A1A1A' }}>That's not right.</strong> The technology exists. The benefits are proven. Why should business size determine who gets to automate?
                  </p>
                </div>
              </motion.div>

              {/* Right: blockquote */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={storyVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid var(--color-border)',
                  borderRadius: '16px',
                  padding: 'clamp(1.5rem, 3vw, 2.5rem)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <blockquote style={{
                  fontSize: '1.15rem',
                  lineHeight: 1.7,
                  color: '#1A1A1A',
                  fontStyle: 'italic',
                  fontFamily: '"DM Sans", sans-serif',
                  margin: 0,
                }}>
                  "Every Indian business deserves the same automation advantages that large companies have. We're here to make that happen."
                </blockquote>
                <p style={{
                  fontSize: '0.85rem',
                  color: '#666666',
                  marginTop: '1rem',
                  fontWeight: 600,
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  — Founder & CEO
                </p>
              </motion.div>
            </div>
          </div>

          <style>{`
            @media (min-width: 768px) {
              .about-story-grid { grid-template-columns: 1.2fr 1fr !important; gap: 4rem !important; }
            }
          `}</style>
        </section>

        {/* Mission & Approach */}
        <section ref={missionRef} style={{ backgroundColor: '#F5F0E8', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              animate={missionVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#1A1A1A',
                textAlign: 'center',
                marginBottom: 'clamp(2.5rem, 4vw, 4rem)',
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Our mission & approach
            </motion.h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }} className="mission-grid">
              {[
                { title: 'Our Mission', desc: 'To make enterprise-level AI automation accessible and affordable for every Indian small business, regardless of their technical expertise or budget.' },
                { title: 'Our Approach', desc: 'We don\'t sell generic tools. We build custom automation systems that fit your exact workflow, language, and business culture. No templates, no compromises.' },
                { title: 'Our Promise', desc: 'You see the automation working before you pay. If we can\'t deliver results that make your business more efficient, you don\'t pay a rupee.' },
              ].map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  animate={missionVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1, ease: 'easeOut' }}
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid var(--color-border)',
                    borderRadius: '16px',
                    padding: 'clamp(1.5rem, 3vw, 2rem)',
                  }}
                >
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.5rem', fontFamily: '"DM Sans", sans-serif' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <style>{`
            @media (min-width: 768px) {
              .mission-grid { grid-template-columns: repeat(3, 1fr) !important; }
            }
          `}</style>
        </section>

        {/* Values */}
        <section ref={valuesRef} style={{ backgroundColor: '#FAF7F2', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              animate={valuesVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#1A1A1A',
                textAlign: 'center',
                marginBottom: 'clamp(2.5rem, 4vw, 4rem)',
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              What we stand for
            </motion.h2>

            <div style={{ borderTop: '1px solid var(--color-border)' }}>
              {VALUES.map((value, idx) => (
                <motion.div
                  key={value.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={valuesVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.1, ease: 'easeOut' }}
                  style={{
                    padding: '1.5rem 0',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '1rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif' }}>
                    {value.name}
                  </span>
                  <span style={{ fontSize: '0.95rem', color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>
                    — {value.description}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{ backgroundColor: '#111111', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              marginBottom: '1rem',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Ready to experience the Rishant difference?
            </h2>
            <p style={{
              fontSize: '1rem',
              lineHeight: 1.65,
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '2rem',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Book a free consultation and see for yourself why Indian businesses choose us.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem' }}>
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '14px 28px', backgroundColor: '#FFFFFF', color: '#111111',
                  borderRadius: '999px', fontSize: '0.95rem', fontWeight: 600,
                  textDecoration: 'none', fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Book Free Audit <HiChevronRight size={18} />
              </Link>
              <Link
                to="/services"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '14px 28px', backgroundColor: 'transparent', color: '#FFFFFF',
                  border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: '999px',
                  fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Explore Services
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;
